import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Mail, Phone, User, MessageSquare, Send, CheckCircle, AlertCircle } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

// Declare grecaptcha global for TypeScript
declare global {
  interface Window {
    grecaptcha: any
  }
}

interface FormData {
  from: string
  phone: string
  name: string
  body: string
  detectedIP?: string
  CaptchaToken?: string
}

interface Mail {
  From: string
  To: string
  Subject: string
  Name?: string | null
  Phone: string
  Body: string
  CaptchaToken?: string
  detectedIP?: string
}


const ContactForm: React.FC = () => {
  const { t } = useLanguage()
  
  const [formData, setFormData] = useState<FormData>({
    from: '',
    phone: '',
    name: '',
    body: '',
    detectedIP: '',
    CaptchaToken: ''
  })

  const [errors, setErrors] = useState<Partial<FormData>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)

  // Load reCAPTCHA v3 script
  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/api.js?render=6LfgGncrAAAAAL02_3ic0Z-2iHXGch2ARxjzpHIz'
    script.async = true
    script.defer = true
    script.onload = () => {
      setRecaptchaLoaded(true)
      // Initialize reCAPTCHA v3
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          // console.log('reCAPTCHA v3 ready')
        })
      }
    }
    document.head.appendChild(script)

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src*="recaptcha/api.js"]')
      if (existingScript) {
        document.head.removeChild(existingScript)
      }
    }
  }, [])

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Phone validation (supports international format with +)
  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^\+?[\d\s\-\(\)]{7,20}$/
    return phoneRegex.test(phone)
  }

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {}

    // Email validation
    if (!formData.from.trim()) {
      newErrors.from = 'Email is required'
    } else if (!validateEmail(formData.from)) {
      newErrors.from = 'Please enter a valid email address'
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (use + for country code)'
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    }

    // Message validation
    if (!formData.body.trim()) {
      newErrors.body = 'Message is required'
    } else if (formData.body.trim().length < 10) {
      newErrors.body = 'Message must be at least 10 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }
  }

  // Get user's IP address
  const getUserIP = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip
    } catch (error) {
      console.error('Error fetching IP:', error)
      return ''
    }
  }

  // Execute reCAPTCHA v3
  const executeRecaptcha = async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA not loaded'))
        return
      }

      window.grecaptcha.ready(() => {
        window.grecaptcha.execute('6LfgGncrAAAAAL02_3ic0Z-2iHXGch2ARxjzpHIz', { action: 'contact_form' })
          .then((token: string) => {
            console.log('reCAPTCHA v3 token received:', token)
            resolve(token)
          })
          .catch((error: any) => {
            console.error('reCAPTCHA v3 execution error:', error)
            reject(error)
          })
      })
    })
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      // Check if reCAPTCHA is loaded
      if (!window.grecaptcha) {
        throw new Error('reCAPTCHA is not loaded. Please refresh the page and try again.')
      }

      // Execute reCAPTCHA v3
      const recaptchaToken = await executeRecaptcha()
      
      if (!recaptchaToken) {
        throw new Error('reCAPTCHA validation failed. Please try again.')
      }

      // Get user's IP address
      const userIP = await getUserIP()
      
      // Prepare mail data (matching your Angular service format)
      const mailData = {
        From: formData.from,
        To: 'info@logicloud.mx',
        Subject: 'Contact Form Submission from LogiCloud Website',
        Name: formData.name,
        Phone: formData.phone,
        Body: formData.body,
        CaptchaToken: recaptchaToken,
        detectedIP: userIP,
        city: null,
        ipList: null
      }

      console.log('Contact form - sending request with data:', mailData)

      // Send email using your API
      const response = await fetch('https://api.programmingalternatives.com/api/v1/Comms/SendMail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          'Authorization': 'Basic Mzo5ODUxRjgwODNERTI1Q0QwNUFDOTBDMzI4RjQ3MTFENDlDRURGNDE5OEI1NkIzQTZDQkY2MEY4RjBGMjUzREVD'
        },
        body: JSON.stringify(mailData)
      })

      const result = await response.json()
      console.log('Contact form response:', result)

      // Handle different response formats (matching your Angular logic)
      let isSuccess = false
      if (result && typeof result === 'object') {
        if (result.success !== undefined) {
          isSuccess = result.success
        } else if (result.status !== undefined) {
          isSuccess = result.status === 'success' || result.status === 200
        } else if (result.message || result.data) {
          isSuccess = true
        } else {
          isSuccess = true
        }
      } else if (result === null || result === undefined) {
        isSuccess = false
      } else {
        isSuccess = !!result
      }

      if (isSuccess) {
        setSubmitStatus('success')
        setSubmitMessage(t('contact.form.successMessage'))
        setFormData({
          from: '',
          phone: '',
          name: '',
          body: '',
          detectedIP: '',
          CaptchaToken: ''
        })
      } else {
        setSubmitStatus('error')
        setSubmitMessage(t('contact.form.errorMessage'))
      }
    } catch (error: any) {
      console.error('Contact form error:', error)
      setSubmitStatus('error')
      
      // Handle different error scenarios (matching your Angular logic)
      let errorMessage = 'Failed to send message. Please try again.'
      if (error && error.message) {
        errorMessage = error.message
      } else if (error && error.status === 401) {
        errorMessage = 'Authentication failed. Please try again.'
      } else if (error && error.status === 403) {
        errorMessage = 'Access denied. Please try again.'
      } else if (error && error.status >= 500) {
        errorMessage = 'Server error. Please try again later.'
      }
      
      setSubmitMessage(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('contact.title')}</h2>
          <p className="text-gray-600 text-lg">
            {t('contact.description').replace('info@logicloud.mx', '').trim()}{' '}
            <span className="text-primary-600 font-semibold">info@logicloud.mx</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Field */}
          <div>
            <label htmlFor="from" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('contact.form.email')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="email"
                id="from"
                name="from"
                value={formData.from}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.from ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('contact.form.emailPlaceholder')}
              />
            </div>
            {errors.from && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.from}
              </p>
            )}
          </div>

          {/* Phone Field */}
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('contact.form.phone')} <span className="text-red-500">*</span>
              <span className="text-gray-500 text-xs ml-1">{t('contact.form.phoneHint')}</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('contact.form.phonePlaceholder')}
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.phone}
              </p>
            )}
          </div>

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('contact.form.name')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('contact.form.namePlaceholder')}
              />
            </div>
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.name}
              </p>
            )}
          </div>

          {/* Message Field */}
          <div>
            <label htmlFor="body" className="block text-sm font-semibold text-gray-700 mb-2">
              {t('contact.form.message')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 text-gray-400 h-5 w-5" />
              <textarea
                id="body"
                name="body"
                value={formData.body}
                onChange={handleInputChange}
                rows={5}
                className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none ${
                  errors.body ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('contact.form.messagePlaceholder')}
              />
            </div>
            {errors.body && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.body}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            type="submit"
            disabled={isSubmitting || !recaptchaLoaded}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200 flex items-center justify-center ${
              isSubmitting || !recaptchaLoaded
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-700 shadow-lg hover:shadow-xl'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('contact.form.sending')}
              </>
            ) : !recaptchaLoaded ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('contact.form.loading')}
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                {t('contact.form.sendButton')}
              </>
            )}
          </motion.button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center"
            >
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-green-800">{submitMessage}</span>
            </motion.div>
          )}

          {submitStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center"
            >
              <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-red-800">{submitMessage}</span>
            </motion.div>
          )}
        </form>
      </motion.div>
    </div>
  )
}

export default ContactForm
